import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight, Wallet } from "lucide-react";

export default function CustomerLogin() {
  const { loginCustomer } = useAuth();
  const navigate = useNavigate();
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const res = await loginCustomer(mobile.trim(), password);
    setSubmitting(false);
    if (res.ok) {
      toast.success(`Welcome back, ${res.user.name}`);
      navigate("/dashboard", { replace: true });
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      {/* Hero side */}
      <div className="relative hidden lg:flex flex-col justify-between bg-slate-950 text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-30 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://images.pexels.com/photos/9800323/pexels-photo-9800323.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=940)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/70 via-slate-950/40 to-slate-950/90" />
        <div className="relative p-10 z-10">
          <div className="flex items-center gap-2">
            <Wallet className="h-5 w-5" />
            <span className="font-display text-lg font-bold tracking-tight">
              ThankF
            </span>
          </div>
        </div>
        <div className="relative p-10 z-10 max-w-md">
          <p className="label-overline text-slate-300">Customer portal</p>
          <h1 className="font-display text-5xl font-extrabold leading-tight mt-3">
            Your balance, <br />
            in plain sight.
          </h1>
          <p className="text-slate-300 mt-4 leading-relaxed">
            Sign in with your registered mobile number to review your current
            balance and complete transaction history.
          </p>
        </div>
        <div className="relative p-10 z-10 label-overline text-slate-400">
          A ThankF product — secure customer wallet
        </div>
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center p-8 bg-grid">
        <div className="w-full max-w-md">
          <div className="mb-10 lg:hidden flex items-center gap-2">
            <Wallet className="h-5 w-5" />
            <span className="font-display text-lg font-bold">ThankF</span>
          </div>
          <p className="label-overline text-slate-500">Customer sign in</p>
          <h2 className="font-display text-4xl font-extrabold mt-2 text-slate-900">
            Welcome back.
          </h2>
          <p className="text-slate-600 mt-2">
            Enter your mobile number and password to continue.
          </p>

          <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="customer-login-form">            <div className="space-y-2">
              <Label htmlFor="mobile" className="label-overline text-slate-700">
                Mobile number
              </Label>
              <Input
                id="mobile"
                data-testid="customer-mobile-input"
                inputMode="numeric"
                placeholder="9876543210"
                value={mobile}
                onChange={(e) => setMobile(e.target.value.replace(/[^0-9+]/g, ""))}
                className="font-mono-num h-12 text-base"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="label-overline text-slate-700">
                Password
              </Label>
              <Input
                id="password"
                data-testid="customer-password-input"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12 text-base"
                required
              />
            </div>
            <Button
              type="submit"
              data-testid="customer-login-submit"
              disabled={submitting}
              className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-medium rounded-md gap-2"
            >
              {submitting ? "Signing in…" : "Sign in"}
              {!submitting && <ArrowRight className="h-4 w-4" />}
            </Button>
          </form>

          <div className="mt-10 pt-6 border-t border-slate-200 flex items-center justify-between">
            <p className="text-sm text-slate-500">New here?</p>
            <Link
              to="/register"
              data-testid="goto-register-link"
              className="text-sm font-medium text-slate-900 hover:underline underline-offset-4"
            >
              Create a customer account →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
